import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';

      sections.forEach((section) => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.clientHeight;
        if (window.scrollY >= sectionTop - 200) {
          current = section.getAttribute('id');
        }
      });

      setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const tableOfContents = [
    { id: 'agreement', title: 'Agreement to Terms' },
    { id: 'platform', title: 'Platform Description' },
    { id: 'user-responsibilities', title: 'User Responsibilities' },
    { id: 'prohibited', title: 'Prohibited Activities' },
    { id: 'rental-terms', title: 'Rental Terms' },
    { id: 'payment', title: 'Payment Terms' },
    { id: 'liability', title: 'Limitation of Liability' },
    { id: 'disputes', title: 'Dispute Resolution' },
    { id: 'termination', title: 'Account Termination' },
    { id: 'changes', title: 'Changes to Terms' },
    { id: 'contact', title: 'Contact Information' }
  ];

  return (
    <>
      <Helmet>
        <title>Terms of Service - VestrioZ</title>
        <meta
          name="description"
          content="Read VestrioZ's terms of service to understand the rules and guidelines for using our platform."
        />
        <meta name="keywords" content="terms of service, user agreement, VestrioZ" />
      </Helmet>
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1EB' }}>
        {/* Hero Section */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#393E46' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent"></div>
          <div className="relative max-w-6xl mx-auto px-4 py-16">
            <Link
              to="/"
              className="inline-flex items-center text-white/80 hover:text-white transition-all duration-300 mb-8 group"
            >
              <i className="fas fa-arrow-left mr-2 group-hover:-translate-x-1 transition-transform"></i>
              Back to VestrioZ
            </Link>
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 animate-fade-in">
                Terms of Service
              </h1>
              <p className="text-xl text-white/90 mb-4">
                Please read these terms carefully before using VestrioZ
              </p>
              <p className="text-white/70">
                Last updated: {new Date().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Table of Contents - Sticky Sidebar */}
            <div className="lg:w-1/4">
              <div className="sticky top-8">
                <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4" style={{ borderColor: '#948979' }}>
                  <h3 className="text-lg font-bold mb-4" style={{ color: '#222831' }}>
                    Table of Contents
                  </h3>
                  <nav className="space-y-2">
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${activeSection === item.id
                          ? 'text-white shadow-md'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        style={activeSection === item.id ? { backgroundColor: '#948979' } : {}}
                      >
                        {item.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-3/4">
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-8 lg:p-12 space-y-12">

                  {/* Agreement to Terms */}
                  <section id="agreement" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-handshake text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Agreement to Terms
                      </h2>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border-l-4" style={{ borderColor: '#948979' }}>
                      <div className="flex items-start">
                        <i className="fas fa-info-circle text-2xl text-blue-500 mr-4 mt-1"></i>
                        <p className="text-gray-700 leading-relaxed text-lg">
                          By accessing and using <span className="font-semibold" style={{ color: '#222831' }}>VestrioZ</span>, you accept and agree to be bound by the terms and provision
                          of this agreement. If you do not agree to abide by the above, please do not use this service.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Platform Description */}
                  <section id="platform" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-tshirt text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Platform Description
                      </h2>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl">
                      <div className="flex items-start">
                        <i className="fas fa-gem text-3xl text-purple-500 mr-4 mt-1"></i>
                        <div>
                          <p className="text-gray-700 leading-relaxed text-lg mb-4">
                            <span className="font-semibold" style={{ color: '#222831' }}>VestrioZ</span> is a fashion and jewellery sharing platform that allows users to rent and share clothing
                            and accessories. We facilitate connections between item owners and renters but are not directly
                            involved in the actual rental transactions.
                          </p>
                          <div className="bg-yellow-100 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                            <p className="text-yellow-800 text-sm">
                              <i className="fas fa-lightbulb mr-2"></i>
                              We act as a marketplace connecting fashion enthusiasts - transactions are between users.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* User Responsibilities */}
                  <section id="user-responsibilities" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-user-check text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        User Responsibilities
                      </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-white border rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex items-center mb-4">
                          <i className="fas fa-shield-alt text-2xl mr-3" style={{ color: '#948979' }}></i>
                          <h3 className="text-xl font-semibold" style={{ color: '#393E46' }}>
                            Account Security
                          </h3>
                        </div>
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <i className="fas fa-key text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Maintain the confidentiality of your account credentials</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-user-edit text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Provide accurate and up-to-date information</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-exclamation-triangle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Notify us immediately of any unauthorized use</span>
                          </li>
                        </ul>
                      </div>
                      <div className="bg-white border rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex items-center mb-4">
                          <i className="fas fa-clipboard-list text-2xl mr-3" style={{ color: '#948979' }}></i>
                          <h3 className="text-xl font-semibold" style={{ color: '#393E46' }}>
                            Content Guidelines
                          </h3>
                        </div>
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <i className="fas fa-check-circle text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Post only authentic, accurate item descriptions</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-camera text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Use your own photos or have permission to use images</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-copyright text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Respect intellectual property rights</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-comments text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Maintain respectful communication with other users</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Prohibited Activities */}
                  <section id="prohibited" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#dc2626' }}>
                        <i className="fas fa-ban text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Prohibited Activities
                      </h2>
                    </div>
                    <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-xl mb-6">
                      <div className="flex items-center mb-2">
                        <i className="fas fa-exclamation-triangle text-red-500 mr-2"></i>
                        <p className="text-red-800 font-semibold">The following activities are strictly prohibited:</p>
                      </div>
                    </div>
                    <div className="grid gap-4">
                      {[
                        { icon: 'fas fa-times-circle', text: 'Posting counterfeit or stolen items', severity: 'high' },
                        { icon: 'fas fa-user-times', text: 'Engaging in fraudulent transactions', severity: 'high' },
                        { icon: 'fas fa-angry', text: 'Harassing or threatening other users', severity: 'high' },
                        { icon: 'fas fa-money-bill-wave', text: 'Attempting to circumvent platform fees', severity: 'medium' },
                        { icon: 'fas fa-gavel', text: 'Using the platform for illegal activities', severity: 'high' },
                        { icon: 'fas fa-clone', text: 'Creating multiple accounts to manipulate the system', severity: 'medium' }
                      ].map((item, index) => (
                        <div key={index} className={`flex items-center p-4 rounded-lg border-l-4 ${item.severity === 'high' ? 'bg-red-50 border-red-500' : 'bg-orange-50 border-orange-500'
                          }`}>
                          <i className={`${item.icon} text-xl mr-4 ${item.severity === 'high' ? 'text-red-500' : 'text-orange-500'
                            }`}></i>
                          <span className="text-gray-700">{item.text}</span>
                          <span className={`ml-auto px-2 py-1 rounded-full text-xs font-semibold ${item.severity === 'high' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                            {item.severity === 'high' ? 'SEVERE' : 'WARNING'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Rental Terms */}
                  <section id="rental-terms" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-exchange-alt text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Rental Terms
                      </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border rounded-xl p-6 shadow-md">
                        <div className="flex items-center mb-4">
                          <i className="fas fa-user-tie text-2xl mr-3 text-blue-600"></i>
                          <h3 className="text-xl font-semibold text-blue-800">
                            For Item Owners
                          </h3>
                        </div>
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <i className="fas fa-sparkles text-blue-500 mt-1 mr-3 text-sm"></i>
                            <span>Ensure items are clean and in described condition</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-clock text-blue-500 mt-1 mr-3 text-sm"></i>
                            <span>Respond promptly to rental requests</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-handshake text-blue-500 mt-1 mr-3 text-sm"></i>
                            <span>Honor confirmed rental agreements</span>
                          </li>
                        </ul>
                      </div>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 border rounded-xl p-6 shadow-md">
                        <div className="flex items-center mb-4">
                          <i className="fas fa-user text-2xl mr-3 text-green-600"></i>
                          <h3 className="text-xl font-semibold text-green-800">
                            For Renters
                          </h3>
                        </div>
                        <ul className="space-y-3 text-gray-700">
                          <li className="flex items-start">
                            <i className="fas fa-undo text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Return items in the same condition as received</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-calendar-check text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Return items by the agreed-upon date</span>
                          </li>
                          <li className="flex items-start">
                            <i className="fas fa-credit-card text-green-500 mt-1 mr-3 text-sm"></i>
                            <span>Pay all agreed-upon fees promptly</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Payment Terms */}
                  <section id="payment" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-credit-card text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Payment Terms
                      </h2>
                    </div>
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-xl mb-6">
                      <div className="flex items-center mb-4">
                        <i className="fas fa-shield-alt text-2xl text-indigo-600 mr-3"></i>
                        <p className="text-gray-700 leading-relaxed text-lg">
                          All payments are processed securely through <span className="font-semibold text-indigo-600">Razorpay</span>. By using our platform, you agree to:
                        </p>
                      </div>
                    </div>
                    <div className="grid gap-4">
                      {[
                        { icon: 'fas fa-star', text: 'Pay all applicable fees for premium listings' },
                        { icon: 'fas fa-user-check', text: 'Provide accurate payment information' },
                        { icon: 'fas fa-receipt', text: 'Accept responsibility for all charges on your account' },
                        { icon: 'fas fa-undo-alt', text: 'Understand that refunds are subject to our refund policy' }
                      ].map((item, index) => (
                        <div key={index} className="flex items-center p-4 bg-white border rounded-lg shadow-sm hover:shadow-md transition-shadow">
                          <i className={`${item.icon} text-xl mr-4`} style={{ color: '#948979' }}></i>
                          <span className="text-gray-700">{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* Limitation of Liability */}
                  <section id="liability" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#dc2626' }}>
                        <i className="fas fa-exclamation-triangle text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Limitation of Liability
                      </h2>
                    </div>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl">
                      <div className="flex items-start">
                        <i className="fas fa-info-circle text-2xl text-yellow-600 mr-4 mt-1"></i>
                        <p className="text-gray-700 leading-relaxed text-lg">
                          <span className="font-semibold" style={{ color: '#222831' }}>VestrioZ</span> acts as a platform to connect users. We are not responsible for the quality, safety,
                          or legality of items listed, the truth or accuracy of listings, or the ability of users to
                          complete transactions. <span className="font-semibold text-yellow-800">Users engage in transactions at their own risk.</span>
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Dispute Resolution */}
                  <section id="disputes" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-balance-scale text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Dispute Resolution
                      </h2>
                    </div>
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-xl">
                      <p className="text-gray-700 leading-relaxed text-lg">
                        In case of disputes between users, we encourage <span className="font-semibold text-blue-800">direct communication first</span>. If needed,
                        we may provide mediation assistance, but users are ultimately responsible for resolving
                        their own disputes.
                      </p>
                    </div>
                  </section>

                  {/* Termination */}
                  <section id="termination" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-user-times text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Account Termination
                      </h2>
                    </div>
                    <div className="bg-red-50 border-l-4 border-red-400 p-6 rounded-r-xl">
                      <p className="text-gray-700 leading-relaxed text-lg">
                        We reserve the right to suspend or terminate accounts that violate these terms. Users may
                        also delete their accounts at any time through their profile settings.
                      </p>
                    </div>
                  </section>

                  {/* Changes to Terms */}
                  <section id="changes" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#393E46' }}>
                        <i className="fas fa-edit text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Changes to Terms
                      </h2>
                    </div>
                    <div className="bg-purple-50 border-l-4 border-purple-400 p-6 rounded-r-xl">
                      <p className="text-gray-700 leading-relaxed text-lg">
                        We may modify these terms at any time. Users will be notified of significant changes,
                        and continued use of the platform constitutes acceptance of the modified terms.
                      </p>
                    </div>
                  </section>

                  {/* Contact */}
                  <section id="contact" className="scroll-mt-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4" style={{ backgroundColor: '#948979' }}>
                        <i className="fas fa-envelope text-white text-lg"></i>
                      </div>
                      <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                        Contact Information
                      </h2>
                    </div>
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-8 rounded-2xl border shadow-lg">
                      <div className="grid md:grid-cols-3 gap-6">
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                            <i className="fas fa-envelope text-white text-xl"></i>
                          </div>
                          <h4 className="font-semibold mb-2" style={{ color: '#222831' }}>Email</h4>
                          <p className="text-gray-600">legal@vestrioz.com</p>
                        </div>
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#393E46' }}>
                            <i className="fas fa-users text-white text-xl"></i>
                          </div>
                          <h4 className="font-semibold mb-2" style={{ color: '#222831' }}>Team</h4>
                          <p className="text-gray-600">VestrioZ Legal Team</p>
                        </div>
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                            <i className="fas fa-clock text-white text-xl"></i>
                          </div>
                          <h4 className="font-semibold mb-2" style={{ color: '#222831' }}>Response Time</h4>
                          <p className="text-gray-600">Within 48 hours</p>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Navigation */}
          <div className="mt-12 text-center">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#222831' }}>
                Ready to join VestrioZ?
              </h3>
              <p className="text-gray-600 mb-6">
                Start sharing and renting amazing fashion pieces today!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1"
                  style={{ backgroundColor: '#222831', color: 'white' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#222831'}
                >
                  <i className="fas fa-home mr-2"></i>
                  Return to VestrioZ
                </Link>
                <Link
                  to="/privacy-policy"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-semibold transition-all duration-300 border-2 hover:shadow-lg hover:-translate-y-1"
                  style={{ borderColor: '#948979', color: '#948979' }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#948979';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#948979';
                  }}
                >
                  <i className="fas fa-shield-alt mr-2"></i>
                  Privacy Policy
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default TermsOfService;